import React, {useEffect} from 'react';
import './styles/App.css';
import './styles/variables.css';
import {QueryClient, QueryClientProvider} from 'react-query';
import ThemeProvider from 'providers/theme';
import {RouterProvider} from 'react-router-dom';
import {router} from 'router';
import {UserContextProvider} from 'providers/user-context';
import {FilterContextProvider} from 'providers/filter-context';
import {Toaster} from 'components/chakra-ui/toaster';
import ApiModal from 'components/api-modal';
import {ModalContextProvider} from './providers/modal-context';
import {TOKEN_KEY, FIRST_URL, FIRST_REFERRER, LAST_REFERRER, DEVICE_ID, SESSION_ID} from './constants';
import { nanoId } from 'helpers';
import { sendWebEvent } from 'api/user';
import { HandleAppInstance } from 'helpers/firebase-analytics-tracker';
import { AdvertisingId } from '@capacitor-community/advertising-id';
import { Capacitor } from '@capacitor/core';

// Global callback for reCAPTCHA v3
declare global {
  interface Window {
    onRecaptchaLoad?: () => void; // Make it optional with ?
    grecaptcha?: any; // Add grecaptcha to avoid @ts-ignore
  }
}
// Function to fire reCAPTCHA v3 pageload event, it will be called when the app mounts (send to google and save result token in in sessionStorage)
window.onRecaptchaLoad = () => {
  // At this point, grecaptcha is guaranteed to exist because Google's script 
  // only calls this callback after it has initialized everything
  window.grecaptcha.ready(() => {
    window.grecaptcha
      .execute('6LezTwwrAAAAAATZ5zXxYPE1cQT7JP7ucpJJLum6', {action: 'pageload'})
      .then((token: string) => {
        sessionStorage.setItem('recaptcha_token', token);
      });
  });
};


function App() {
  // Get token from URL param (useful for cases like verification, and reset password which relies on token provided in the URL)
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';

  // Define base React query options
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
      }
    }
  });

  // Save first_url to localStorage when app mounts
  useEffect(() => {
    const existing = localStorage.getItem(FIRST_URL);
    if (!existing) {
      const currentUrl = window.location.href;
      localStorage.setItem(FIRST_URL, currentUrl);
    }
  }, []);

  // Save first_referrer to localStorage when app mounts
  useEffect(() => {
    const existing = localStorage.getItem(FIRST_REFERRER);
    if (!existing) {
      const referrer = document.referrer;
      localStorage.setItem(FIRST_REFERRER, referrer);
    }
  }, []);

  // Save last_referrer to localStorage when app mounts (always update if referrer accessable)
  useEffect(() => {
    const referrer = document.referrer;
    if (referrer) {
      localStorage.setItem(LAST_REFERRER, referrer);
    }
  }
  , []);

  // Generate device_id and save it to localStorage when app mounts
  useEffect(() => {
    const existing = localStorage.getItem(DEVICE_ID);
    if (!existing) {
      const deviceId = nanoId();
      localStorage.setItem(DEVICE_ID, deviceId);
    }
  }, []);

  // Generate session_id and save it to sessionStorage when app mounts
  useEffect(() => {
    const existing = sessionStorage.getItem(SESSION_ID);
    if (!existing) {
      const sessionId = nanoId();
      sessionStorage.setItem(SESSION_ID, sessionId);
    }
  }
  , []);

  // Update token in localStorage when token changes
  useEffect(() => {
    if (!token) {
      return;
    }
    localStorage.setItem(TOKEN_KEY, token);
  }, [token]);

  // Fire web analytics event *after* all useEffects, layout, and critical tasks are done.
  // Or, if not finised, wait 200ms delay.
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        void sendWebEvent('pageload');
      });
    } else {
      // Fallback: wait 200ms before sending the event
      setTimeout(() => {
        void sendWebEvent('pageload');
      }, 200);
    }
  }, []);

  // Load reCAPTCHA v3 script on app mount (+ onload callback will be fired, specified in the url, it will call the global function window.onRecaptchaLoad)
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=6LezTwwrAAAAAATZ5zXxYPE1cQT7JP7ucpJJLum6&onload=onRecaptchaLoad';
    script.async = true;
    document.head.appendChild(script);
  }
  , []);

  // Get and save Advertising ID (only used in android gaid for now) on local storage to be used later when user reqeust for offers (we do it here once per app load)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    if (localStorage.getItem('gaid')) {
      // gaid already exists in localStorage, skipping fetch
      return;
    }

    async function getAdvertisingId() {
      // Safe to call on both platforms; on iOS this returns IDFA only if authorized.
      const { id, status } = await AdvertisingId.getAdvertisingId().catch((error) => {return { id: null, status: 'unknown' }; });
      // On Android, status is typically 'Authorized'. If Play Services missing or restricted, handle gracefully.
      if (id) {
        // Save advertising ID (gaid in android) to sessionStorage
        localStorage.setItem('gaid', id);
      } else {
        // No Advertising ID available
      }
    }
    getAdvertisingId();
  }
  , []);

  //render component and wrap main app with context providers
  return (
    <QueryClientProvider client={queryClient}>
      <ModalContextProvider>
        <ThemeProvider>
          <FilterContextProvider>
            <UserContextProvider>
              <>
                <RouterProvider router={router} />
                <Toaster />
                <ApiModal />
              </>
            </UserContextProvider>
          </FilterContextProvider>
        </ThemeProvider>
      </ModalContextProvider>
    </QueryClientProvider>
  );
}

export default App;
